/**
 * Dialog component for saving current search filters
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

interface SaveSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, description?: string) => Promise<void>;
  entityType: "organizations" | "facilities" | "projects" | "sources";
  activeFilterCount: number;
}

const SaveSearchDialog: React.FC<SaveSearchDialogProps> = ({
  open,
  onClose,
  onSave,
  entityType,
  activeFilterCount,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Search name is required");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(name.trim(), description.trim() || undefined);
      // Reset form on success
      setName("");
      setDescription("");
      onClose();
    } catch (err) {
      setError("Failed to save search. Please try again.");
      return;
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setName("");
      setDescription("");
      setError(null);
      onClose();
    }
  };

  const entityTypeLabels = {
    organizations: "Organizations",
    facilities: "Facilities",
    projects: "Projects",
    sources: "Sources",
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Search</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Save your current search filters for quick access later.
        </DialogContentText>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Entity Type:</strong> {entityTypeLabels[entityType]}
          <br />
          <strong>Active Filters:</strong> {activeFilterCount}
        </Typography>

        <TextField
          autoFocus
          margin="dense"
          label="Search Name"
          type="text"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={isSaving}
          placeholder="e.g., Steel manufacturers in North America"
        />

        <TextField
          margin="dense"
          label="Description (optional)"
          type="text"
          fullWidth
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSaving}
          placeholder="Add notes about this search..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || !name.trim()}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveSearchDialog;
